/**
 * ============================================================================
 * Specifikit Core JS
 * Location: assets/specifikit-core.js
 * Version: 1.0
 * Author: specifikit.com
 * ============================================================================
 */

// IDEMPOTENT SCRIPT GUARD
// This check prevents the entire script from being parsed and executed a second time
// if it's included more than once on a page (e.g., in theme.liquid and a section).
if (!window.SpecifikitCore) {
    
/**
 * ============================================================================
 * SpecifikitLogger v1.0
 * A robust, conditional, and styled logging utility for Specifikit.
 * It provides leveled logging, styled console output, processes server-side
 * logs, and offers intelligent, dynamic diagnostic feedback.
 * ============================================================================
 */
class SpecifikitLogger {
    constructor(debugConfig = {}) {
        // More robust admin check using Shopify's AdminBarInjector
        const isAdmin = !!(window.Shopify?.designMode || window.Shopify?.AdminBarInjector);
        
        // Stricter boolean checks for robustness
        const settingIsEnabled = debugConfig.settingEnabled === true;
        const isAdminOnlyRequired = debugConfig.adminOnly !== false; // Defaults to true if 'adminOnly' is undefined
        const paramOverride = debugConfig.paramOverride === true;
        
        const themeSettingCondition = settingIsEnabled && (!isAdminOnlyRequired || isAdmin);
        
        this.isEnabled = themeSettingCondition || paramOverride;
        
        this.PREFIX = 'Specifikit';
        this.STYLES = {
            prefix: 'font-weight: bold; color: #4338ca;', // Indigo-700
            log: 'color: #374151;',    // Gray-700
            info: 'color: #059669;',   // Green-600
            warn: 'color: #d97706;',   // Amber-600
            error: 'color: #dc2626;',  // Red-600
            timestamp: 'color: #9ca3af; font-size: 0.8em;', // Gray-400
        };

        // START: DEBUGGER DIAGNOSTICS
        if (this.isEnabled) {
            console.log(`%c${this.PREFIX}`, this.STYLES.prefix, 'Debug mode is active. Verbose logs will be shown.');
            if (settingIsEnabled && !isAdminOnlyRequired && !isAdmin) {
                console.warn(`%c${this.PREFIX}`, this.STYLES.prefix, 'Warning: "Require Admin Login" is disabled. Debug logs are publicly visible.');
            }
        } else {
            // Only show diagnostics if there was an *intent* to debug, to avoid spamming the console for regular users.
            const intentToDebug = settingIsEnabled || paramOverride || (window.specifikitServerLogs && window.specifikitServerLogs.length > 0);
            if (intentToDebug) {
                let reason = '';
                if (!settingIsEnabled && !paramOverride) {
                    reason = 'The "Enable Debug Mode" setting is disabled in the Theme Editor and no URL override is present.';
                } else if (isAdminOnlyRequired && !isAdmin) {
                    reason = 'The "Require Admin Login" setting is enabled, and you are not logged in as a Shopify admin.';
                } else {
                    reason = 'An unknown condition prevented logging from being enabled. Please review the diagnostic table below.';
                }
                
                const suggestion = (isAdminOnlyRequired && !isAdmin && !paramOverride) ? ' Try adding ?specifikit_debug=true to the URL to override.' : '';
        
                console.groupCollapsed(`%c${this.PREFIX}`, 'font-weight: bold; color: #d97706;', `Debug logs are inactive.`);
                console.log(`%cReason: ${reason}${suggestion}`, 'color: #d97706;');
                
                console.log('%c--- Live Diagnostic Info ---', 'font-weight: bold; padding: 5px 0;');
                try {
                    console.table({
                        'URL Override Detected (?specifikit_debug=true)': { 
                            'Value Seen by Logger': paramOverride, 
                            'Source': 'URL Parameter -> Liquid -> JSON'
                        },
                        'Debug Mode Enabled in Theme Editor': { 
                            'Value Seen by Logger': settingIsEnabled, 
                            'Source': 'Theme Setting -> Liquid -> JSON'
                        },
                        '"Require Admin Login" Enabled': { 
                            'Value Seen by Logger': isAdminOnlyRequired, 
                            'Source': 'Theme Setting -> Liquid -> JSON'
                        },
                        'User is Logged-in Shopify Admin': { 
                            'Value Seen by Logger': isAdmin, 
                            'Source': 'Browser State (window.Shopify)'
                        },
                        'FINAL DECISION: Show Logs?': { 
                            'Value Seen by Logger': this.isEnabled, 
                            'Source': 'JS Logic'
                        }
                    });
                } catch (e) { // Fallback for browsers that don't support console.table well
                    console.log({ paramOverride, settingIsEnabled, isAdminOnlyRequired, isAdmin, isEnabled: this.isEnabled });
                }
                console.log('Received `debugConfig` object from Liquid:', JSON.parse(JSON.stringify(debugConfig)));
                console.groupEnd();
            }
        }
        // END: DEBUGGER DIAGNOSTICS
    }

    _log(level, message, ...args) {
        if (!this.isEnabled && level !== 'error') {
            return;
        }

        const timestamp = new Date().toLocaleTimeString('en-US', { hour12: false });
        const prefix = `%c${this.PREFIX}`;
        const messageStyle = this.STYLES[level] || this.STYLES.log;
        
        if (args.length === 0 && typeof message === 'string') {
            console[level](`${prefix} %c${message} %c@ ${timestamp}`, this.STYLES.prefix, messageStyle, this.STYLES.timestamp);
        } else {
            const groupLabel = typeof message === 'string' ? message : 'Log Entry';
            console.groupCollapsed(`${prefix} %c${groupLabel} %c@ ${timestamp}`, this.STYLES.prefix, messageStyle, this.STYLES.timestamp);
            if (typeof message !== 'string') console.log(message);
            args.forEach(arg => console.log(arg));
            console.groupEnd();
        }
    }

    log(message, ...args) { this._log('log', message, ...args); }
    info(message, ...args) { this._log('info', message, ...args); }
    warn(message, ...args) { this._log('warn', message, ...args); }
    error(message, ...args) { this._log('error', message, ...args); }
    
    group(label) {
        if (!this.isEnabled) return;
        console.group(`%c${this.PREFIX}: ${label}`, this.STYLES.prefix);
    }
    
    groupCollapsed(label) {
        if (!this.isEnabled) return;
        console.groupCollapsed(`%c${this.PREFIX}: ${label}`, this.STYLES.prefix);
    }
    
    groupEnd() {
        if (!this.isEnabled) return;
        console.groupEnd();
    }

    table(data) {
        if (!this.isEnabled) return;
        console.table(data);
    }

    /**
     * Processes and logs any messages queued from the server-side
     * Liquid render through the unified logging pipeline for consistent output.
     */
    processQueue() {
        const queue = window.specifikitServerLogs;

        if (!queue || !Array.isArray(queue)) {
            return;
        }

        if (this.isEnabled && queue.length > 0) {
            queue.forEach(log => {
                const [level, ...args] = log;
                
                // Map the server-side log levels to the logger's public methods
                switch (level) {
                    case 'log':
                        this.log(...args);
                        break;
                    case 'info':
                        this.info(...args);
                        break;
                    case 'warn':
                        this.warn(...args);
                        break;
                    case 'error':
                        this.error(...args);
                        break;
                    case 'group':
                        this.group(args[0] || 'Group');
                        break;
                    case 'groupCollapsed':
                        this.groupCollapsed(args[0] || 'Collapsed Group');
                        break;
                    case 'groupEnd':
                        this.groupEnd();
                        break;
                    default:
                        // Fallback for any other console methods used server-side
                        if (typeof console[level] === 'function') {
                            console[level](...args);
                        }
                        break;
                }
            });
        }
        
        // Always clear the queue to prevent reprocessing and free memory.
        window.specifikitServerLogs = [];
    }
}

class SpecifikitCore {
  constructor() {
    this.container = null;
    this.ui = null; // UI controller instance
    this.logger = null; // Logger instance
    this.config = {};
    this.state = {
      size: null,
      variantId: null,
      basePrice: 0,
      baseCompareAtPrice: 0, // Track compare at price
      componentSelections: {},
      addons: {},
      view: null,
      isInitialState: false,
    };
    this.mode = 'customizer';
    this.imageCache = new Map();
    this.isSubmitting = false;
    this.conditionalRulesEngine = null;
    this.wishlistObserver = null;

    this.liveProductData = null;
    this.liveAddonData = new Map();
    this.addonProductHandles = [];
    this.isFetchingInventory = false;
    this.isInitialized = false;
    this.isFullyInitialized = false;

    // Properties for the Universal Cart Observer
    this.cartObserver = null;
    this.cartUpdateTimeout = null;
    this.isUpdatingCartImages = false;
    this.isUpdatingCartPrices = false;
    this.watchingForImages = new Set();


    // START: LOGGER INITIALIZATION
    // This block safely parses the config island early just to get the debug
    // configuration. This allows the logger to be active from the very start
    // and process any server-side logs queued in `window.specifikitServerLogs`.
    let debugConfig = {};
    const configEl = document.getElementById('CustomizerConfig');
    if (configEl) {
        try {
            // We only parse for debugConfig here, the full config is loaded later.
            const configData = JSON.parse(configEl.textContent);
            debugConfig = configData.debugConfig || {};
        } catch (e) {
            // A parse error here is not fatal, but we should log it.
            console.error("Specifikit: Failed to parse config for logger initialization.", e);
        }
    }
    
    this.logger = new SpecifikitLogger(debugConfig);
    this.logger.processQueue();
    // END: LOGGER INITIALIZATION

    this.logger.info('🎨 Specifikit Customizer: Core script instance created.');

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    this.container = document.querySelector('[data-specifikit-container]');
    if (this.container) {
      // This is a product page with the customizer
      this.ui = new SpecifikitUI(this);
      if (!this.loadConfig()) {
        this.logger.error('Config load failed');
        this.ui.showConfigError();
        return;
      }
      this.initializeCustomizerUI();
    } else {
      // Not a customizer page, but the script is loaded.
      this.logger.log('Not a product page. Cart observer is active.');
    }

    // ALWAYS initialize the cart observer on every page.
    this.initCartObserver();
  }


  async initializeCustomizerUI() {
    if (this.isInitialized) return;
    this.isInitialized = true;

    await this.setupFullUI();

    if (this.config.displayMode === 'modal') {
      this.ui.setupModalMode();
    }

    this.container.classList.add('specifikit-ready');
    this.logger.info('✅ Initialized', this.state);
  }

  async setupFullUI() {
    if (this.isFullyInitialized) return;
    this.isFullyInitialized = true;

    this.ui.queryElements();
    await this.ui.enhanceProductPage();
    this.ui.queryElements();

    this.ui.validateElements();
    this.ui.setupActionButtonObserver();
    this.ui.setupAccordionLayout();
    this.ui.setupUnifiedViewToolbar();
    this.ui.setupComponentDisplayStyles();
    this.ui.setupSwatches();

    this.initializeConditionalRules();
    this.initializeState();
    this.attachEventListeners();
    this.updateUI();

    this.fetchLiveInventory();
    this.ui.setMode('customizer');
  }

  loadConfig() {
    const mainConfigEl = document.getElementById('CustomizerConfig');
    if (!mainConfigEl) return false;

    try {
      this.config = JSON.parse(mainConfigEl.textContent);
      this.config.initialCompareAtPrice = this.config.initialCompareAtPrice || 0; // Ensure compare price is set

      const sectionSettingsEl = this.container.querySelector('script[data-section-settings]');
      if (sectionSettingsEl) {
        const settings = JSON.parse(sectionSettingsEl.textContent);
        this.config.displayMode = settings.display_mode || 'inline';
        this.config.enableTransplantation = typeof settings.enable_transplantation === 'boolean' ? settings.enable_transplantation : true;
      } else {
        this.config.displayMode = this.container.dataset.displayMode || 'inline';
        this.config.enableTransplantation = true;
        this.logger.warn('Section settings data island not found. Falling back to defaults.');
      }

      const galleryConfigEl = this.container.querySelector('[data-gallery-config]');
      if (galleryConfigEl) {
        this.config.galleryConfig = JSON.parse(galleryConfigEl.textContent);
      } else {
        this.logger.warn('Gallery config data island not found. Using default values.');
        this.config.galleryConfig = {
          border_in_customizer_mode: false,
          border_in_gallery_mode: true,
          info_bar_enabled: true,
          info_bar_hide_on_mobile: true
        };
      }

      if (!this.config.productHandle) {
        const productHandleMatch = window.location.pathname.match(/\/products\/([a-z0-9-]+)/);
        this.config.productHandle = productHandleMatch ? productHandleMatch[1] : null;
      }

      if (!this.config.productId || !this.config.productHandle || !Array.isArray(this.config.components)) {
        this.logger.error('Invalid config structure. Missing productId, productHandle, or components array.');
        return false;
      }

      const uniqueAddonHandles = new Set();
      this.config.addons?.forEach(addon => {
        if (addon.type === 'checkbox' && addon.product_handle) {
          uniqueAddonHandles.add(addon.product_handle);
        }
      });
      this.addonProductHandles = [...uniqueAddonHandles];

      this.state.isInitialState = !!(this.config.initialViewConfig && this.config.initialViewConfig.enable);

      this.logger.info('Config loaded', { DisplayMode: this.config.displayMode, Transplantation: this.config.enableTransplantation });
      return true;
    } catch (e) {
      this.logger.error('JSON parse failed while loading main config:', e);
      return false;
    }
  }

  handleViewChange(viewHandle, triggerStateChange = true) {
    if (this.state.view === viewHandle && this.mode === 'customizer') return;

    // --- SYNCHRONY FAILSAFE ---
    // If a static view is selected, command the advanced module to deactivate 360° view.
    if (window.advancedFeatures && typeof window.advancedFeatures.deactivate360View === 'function') {
      window.advancedFeatures.deactivate360View();
    }
    // -------------------------

    this.ui.setMode('customizer');

    this.state.view = viewHandle;

    // Delegate UI updates to the UI controller
    this.ui.handleViewChangeUI(viewHandle);

    if (this.state.isInitialState) {
      this.state.isInitialState = false;
      this.logger.log('🚪 Exiting initial state due to view change.');
    }

    this.logger.log(`📐 View changed: ${viewHandle}`);
    if (triggerStateChange) {
      this.onVisualStateChange();
    }
  }

  /**
   * Method to handle state change when entering gallery mode.
   * This keeps state logic within the Core class.
   */
  clearViewStateForGallery() {
    if (this.state.view !== null) {
      this.state.view = null;
      this.logger.log("🖼️ View state cleared for gallery mode.");
    }
  }

  async fetchLiveInventory() {
    if (this.isFetchingInventory) return;
    const handlesToFetch = [this.config.productHandle, ...this.addonProductHandles].filter(Boolean);
    if (handlesToFetch.length === 0) return;

    this.logger.log('🔄 Fetching live inventory for:', handlesToFetch);
    this.isFetchingInventory = true;

    try {
      const fetchPromises = handlesToFetch.map(handle =>
        fetch(`/products/${handle}.js`).then(res => {
          if (!res.ok) throw new Error(`Network response was not ok for ${handle}`);
          return res.json();
        })
      );

      const results = await Promise.all(fetchPromises);
      this.liveProductData = results[0];
      this.logger.log('✅ Live inventory data loaded for main product', this.liveProductData);

      this.addonProductHandles.forEach((handle, index) => {
        this.liveAddonData.set(handle, results[index + 1]);
      });
      this.logger.log('✅ Live inventory data loaded for addons', this.liveAddonData);

      this.ui.updateAvailabilityUI();
      this.ui.updateAddonAvailabilityUI();

      // Dispatch a state update event after inventory is loaded.
      // This ensures the pre-order module re-evaluates its status,
      // especially for 'automatic' mode where it might have returned early
      // before live data was available.
      this.dispatchStateUpdateEvent();

    } catch (error) {
      this.logger.error('Failed to fetch live inventory:', error);
      this.ui.showNotification('Could not fetch live stock information. Please check your connection.', 'error');
    } finally {
      this.isFetchingInventory = false;
    }
  }

  initializeConditionalRules() {
    if (!this.config.conditionalRules || !Array.isArray(this.config.conditionalRules) || this.config.conditionalRules.length === 0) {
      this.logger.log('ℹ️ No conditional rules configured.');
      return;
    }

    this.conditionalRulesEngine = {
      rules: this.config.conditionalRules,
      evaluate: (currentState) => {
        const results = {
          disable: [],
          enable: [],
          showAddon: [],
          hideAddon: []
        };

        this.rules.forEach(rule => {
          let conditionMet = true;
          if (rule.when) {
            for (const [key, value] of Object.entries(rule.when)) {
              const selection = key === 'size' ? currentState.size : currentState.componentSelections[key];

              let isMet = false;
              if (Array.isArray(selection)) {
                isMet = selection.some(s => s.handle === value);
              } else if (typeof selection === 'object' && selection !== null) {
                isMet = selection.handle === value;
              } else if (typeof selection === 'string') {
                isMet = selection === value;
              }

              if (!isMet) {
                conditionMet = false;
                break;
              }
            }
          }

          if (rule.disable) {
            const reasonParts = [];
            for (const [key, value] of Object.entries(rule.when)) {
              const comp = this.config.components.find(c => c.key === key);
              const opt = this.config.options[key]?.find(o => o.handle === value);
              reasonParts.push(opt?.label || this.capitalize(value));
            }
            const reason = reasonParts.join(' ');

            for (const [componentKey, optionHandles] of Object.entries(rule.disable)) {
              const handles = Array.isArray(optionHandles) ? optionHandles : [optionHandles];
              handles.forEach(handle => {
                if (conditionMet) {
                  results.disable.push({
                    component: componentKey,
                    option: handle,
                    reason
                  });
                } else {
                  results.enable.push({
                    component: componentKey,
                    option: handle
                  });
                }
              });
            }
          }

          const processAddonVisibility = (block, isMet) => {
            if (!block) return;
            if (block.show_addon) {
              const addons = Array.isArray(block.show_addon) ? block.show_addon : [block.show_addon];
              (isMet ? results.showAddon : results.hideAddon).push(...addons);
            }
            if (block.hide_addon) {
              const addons = Array.isArray(block.hide_addon) ? block.hide_addon : [block.hide_addon];
              (isMet ? results.hideAddon : results.showAddon).push(...addons);
            }
          };

          processAddonVisibility(rule.then, conditionMet);
          processAddonVisibility(rule.else, !conditionMet);
        });

        return results;
      }
    };
    this.logger.info(`✅ Conditional rules loaded: ${this.conditionalRulesEngine.rules.length} rules found.`);
  }

  initializeState() {
    this.logger.groupCollapsed('🔄 Initializing State (Pricing & State Engine)');

    let priceInitialized = false;
    const sizeComponent = this.config.components?.find(c => c.key === 'size' && c.enabled);
    let defaultSizeInput = null;

    if (sizeComponent) {
      defaultSizeInput = this.container.querySelector('[data-specifikit-option-input="size"]:checked');

      if (!defaultSizeInput) {
        const firstAvailableSize = this.container.querySelector('[data-specifikit-option-input="size"]:not(:disabled)');
        if (firstAvailableSize) {
          firstAvailableSize.checked = true;
          firstAvailableSize.closest('label')?.classList.add('specifikit-active');
          defaultSizeInput = firstAvailableSize;
          this.logger.warn('No default size selection found. Auto-selecting first available option to ensure correct initial state.');
        }
      }

      if (defaultSizeInput) {
        this.state.size = defaultSizeInput.dataset.size;
        this.state.variantId = defaultSizeInput.value;
      } else {
        this.logger.error('FATAL - All size options are unavailable. Cannot select a default variant.');
        if (this.ui.elements.submitButton) this.ui.elements.submitButton.disabled = true;
      }
    } else {
      const variantInput = this.container.querySelector('[data-specifikit-variant-id-input]');
      if (variantInput) this.state.variantId = variantInput.value;
    }

    if (defaultSizeInput) {
      const sizePrice = parseFloat(defaultSizeInput.dataset.price);
      if (!isNaN(sizePrice)) {
        this.state.basePrice = sizePrice;
        this.state.baseCompareAtPrice = parseFloat(defaultSizeInput.dataset.compareAtPrice || 0);
        priceInitialized = true;
        this.logger.log(`Price Initialized (L1 - Checked Variant): ${this.state.basePrice} | Compare: ${this.state.baseCompareAtPrice}`);
      }
    }

    if (!priceInitialized && typeof this.config.initialPrice === 'number') {
      this.state.basePrice = this.config.initialPrice / 100.0;
      this.state.baseCompareAtPrice = this.config.initialCompareAtPrice / 100.0;
      priceInitialized = true;
      this.logger.log(`Price Initialized (L2 - JSON Config): ${this.state.basePrice} | Compare: ${this.state.baseCompareAtPrice}`);
    }

    if (!priceInitialized) {
      const basePriceEl = this.container.querySelector('[data-base-price]');
      if (basePriceEl && basePriceEl.dataset.basePrice) {
        const domPrice = parseFloat(basePriceEl.dataset.basePrice);
        if (!isNaN(domPrice)) {
          this.state.basePrice = domPrice;
          this.state.baseCompareAtPrice = 0; // Assume no compare price from this legacy method
          priceInitialized = true;
          this.logger.log(`Price Initialized (L3 - DOM Attr): ${this.state.basePrice}`);
        }
      }
    }

    if (!priceInitialized) {
      this.state.basePrice = 0;
      this.state.baseCompareAtPrice = 0;
      this.logger.error('CRITICAL - Could not determine initial base price from any source. Price may be incorrect.');
    }

    const imageConfig = this.config.imageConfig;
    if (imageConfig?.views?.length > 0) {
        const defaultView = imageConfig.views.find(v => v.default === true) || imageConfig.views[0];
        this.state.view = defaultView.handle;
    }

    this.config.components?.forEach(comp => {
      if (!comp.enabled || comp.key === 'size') return;

      const displayStyle = comp.display_style || 'button';

      if (displayStyle === 'checkbox') {
        this.state.componentSelections[comp.key] = [];
        const checkedInputs = this.container.querySelectorAll(`[data-specifikit-option-input="${comp.key}"]:checked`);
        checkedInputs.forEach(input => {
          this.state.componentSelections[comp.key].push({
            handle: input.value,
            name: input.dataset[`${comp.key}Name`] || this.capitalize(input.value),
            priceModifier: parseFloat(input.dataset.priceModifier || 0)
          });
        });
      } else {
        let defaultInput = this.container.querySelector(`[data-specifikit-option-input="${comp.key}"]:checked`);

        if (!defaultInput) {
          const firstAvailableInput = this.container.querySelector(`[data-specifikit-option-input="${comp.key}"]:not(:disabled)`);
          if (firstAvailableInput) {
            firstAvailableInput.checked = true;
            firstAvailableInput.closest('label')?.classList.add('specifikit-active');
            defaultInput = firstAvailableInput;
            this.logger.warn(`No default selection for "${comp.label}". Auto-selecting first available option.`);

            const dropdownWrapper = firstAvailableInput.closest('.specifikit-option-group')?.querySelector('.specifikit-dropdown-wrapper');
            if (dropdownWrapper) {
              this.ui.updateCustomDropdownDisplay(dropdownWrapper, firstAvailableInput.value);
            }
          }
        }

        if (defaultInput) {
          this.state.componentSelections[comp.key] = {
            handle: defaultInput.value,
            name: defaultInput.dataset[`${comp.key}Name`] || this.capitalize(defaultInput.value),
            priceModifier: parseFloat(defaultInput.dataset.priceModifier || 0)
          };
        } else {
          this.logger.error(`No available options found for component "${comp.label}". It will be ignored.`);
        }
      }
    });

    this.config.addons?.forEach(addon => {
      const inputEl = this.container.querySelector(`[data-addon-id="${addon.id}"]`);
      if (!inputEl) return;

      if (addon.type === 'checkbox') {
        this.state.addons[addon.id] = {
          type: 'checkbox',
          enabled: inputEl.checked,
          price: parseFloat(addon.price || 0),
          variantId: addon.variantId,
          productHandle: addon.product_handle,
          label: addon.label
        };
      } else if (addon.type === 'text') {
        this.state.addons[addon.id] = {
          type: 'text',
          value: inputEl.value.trim(),
          price: parseFloat(addon.price || 0),
          variantId: addon.variantId,
          label: addon.label
        };
      }
    });
    this.logger.groupEnd();
  }

  attachEventListeners() {
    this.logger.groupCollapsed('🔗 Attaching Event Listeners');
    let count = 0;
    const { elements } = this.ui;

    const sizeComponent = this.config.components?.find(c => c.key === 'size' && c.enabled);
    if (sizeComponent && elements.componentOptions.size) {
      elements.componentOptions.size.forEach(input => {
        input.addEventListener('change', (e) => this.handleSizeChange(e));
        count++;
      });
    }

    this.config.components?.forEach(comp => {
      if (!comp.enabled || comp.key !== 'size') {
        elements.componentOptions[comp.key]?.forEach(input => {
          input.addEventListener('change', (e) => this.handleComponentChange(comp.key, e));
          count++;
        });
      }
    });

    elements.addonInputs?.forEach(input => {
      if (input.type === 'checkbox') {
        input.addEventListener('change', (e) => this.handleAddonCheckbox(e));
        count++;
      } else if (input.type === 'text') {
        input.addEventListener('input', (e) => this.handleAddonText(e));
        count++;
      }
    });

    if (elements.form) {
      elements.form.addEventListener('submit', (e) => this.handleFormSubmit(e));
      count++;
    }

    if (elements.quantityInput) {
      elements.quantityInput.addEventListener('change', () => this.ui.syncQuantity());
    }

    const quantityContainer = this.container.querySelector('[data-specifikit-placeholder="form-container"]');
    if (quantityContainer) {
      quantityContainer.addEventListener('click', (e) => {
        if (e.target.matches('.quantity__button')) {
          setTimeout(() => this.ui.syncQuantity(), 50);
        }
      });
      quantityContainer.addEventListener('change', (e) => {
        if (e.target.matches('input[name="quantity"]')) {
          this.ui.syncQuantity();
        }
      });
    }

    if (elements.resumeButton) {
      elements.resumeButton.addEventListener('click', () => {
        this.ui.setMode('customizer');
        this.onVisualStateChange();
        elements.productInfoContainer?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      });
      count++;
    }

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.fetchLiveInventory();
      }
    });
    count++;

    this.logger.info(`✅ ${count} listeners attached`);
    this.logger.groupEnd();
  }

  handleSizeChange(e) {
    this.ui.setMode('customizer');

    if (this.state.isInitialState) {
      this.state.isInitialState = false;
      this.logger.log('🚪 Exiting initial state due to size change.');
    }

    const input = e.target;
    this.state.size = input.dataset.size;
    this.state.variantId = input.value;
    this.state.basePrice = parseFloat(input.dataset.price || 0);
    this.state.baseCompareAtPrice = parseFloat(input.dataset.compareAtPrice || 0);

    const optionsContainer = input.closest('[data-specifikit-option-list]');
    if (optionsContainer) {
      optionsContainer.querySelectorAll('label').forEach(l => l.classList.remove('specifikit-active'));
    }
    input.closest('.specifikit-size-option')?.classList.add('specifikit-active');

    if (this.ui.elements.submitButton && !input.disabled) {
      this.ui.elements.submitButton.disabled = false;
    }

    this.logger.log('📏 Size changed:', this.state.size);
    this.onVisualStateChange();
  }

  handleComponentChange(componentKey, e) {
    this.ui.setMode('customizer');

    if (this.state.isInitialState) {
      this.state.isInitialState = false;
      this.logger.log('🚪 Exiting initial state due to component change.');
    }

    const input = e.target;
    const label = input.closest('label');

    const componentConfig = this.config.components.find(c => c.key === componentKey);
    if (!componentConfig) {
      this.logger.error(`Could not find config for component key "${componentKey}".`);
      return;
    }

    const isMultiSelect = componentConfig.display_style === 'checkbox';

    if (isMultiSelect) {
      const currentSelections = this.state.componentSelections[componentKey] || [];
      const selection = {
        handle: input.value,
        name: input.dataset[`${componentKey}Name`] || this.capitalize(input.value),
        priceModifier: parseFloat(input.dataset.priceModifier || 0)
      };

      if (input.checked) {
        if (!currentSelections.some(s => s.handle === selection.handle)) {
          this.state.componentSelections[componentKey] = [...currentSelections, selection];
        }
      } else {
        this.state.componentSelections[componentKey] = currentSelections.filter(
          s => s.handle !== selection.handle
        );
      }

      label?.classList.toggle('specifikit-active', input.checked);
      this.logger.log(`☑️ ${componentKey} changed (Multi-Select):`, this.state.componentSelections[componentKey].map(s => s.handle));

      if (this.config.imageConfig.mode === 'canvas') {
        this.onVisualStateChange();
      } else {
        this.onPriceStateChange();
      }

    } else {
      this.state.componentSelections[componentKey] = {
        handle: input.value,
        name: input.dataset[`${componentKey}Name`] || this.capitalize(input.value),
        priceModifier: parseFloat(input.dataset.priceModifier || 0)
      };

      const optionsContainer = input.closest('[data-specifikit-option-list]');
      if (optionsContainer) {
        optionsContainer.querySelectorAll('label').forEach(l => {
          l.classList.remove('specifikit-active');
        });
      }
      label?.classList.add('specifikit-active');

      this.logger.log(`🔘 ${componentKey} changed (Single-Select):`, this.state.componentSelections[componentKey]);
      this.onVisualStateChange();
    }
  }

  handleAddonCheckbox(e) {
    this.ui.setMode('customizer');
    const input = e.target;
    const addonId = input.dataset.addonId;

    if (this.state.addons[addonId]) {
      this.state.addons[addonId].enabled = input.checked;
      this.logger.log(`✓ Addon ${addonId}:`, input.checked);
      this.onPriceStateChange();
    }
  }

  handleAddonText(e) {
    this.ui.setMode('customizer');
    const input = e.target;
    const addonId = input.dataset.addonId;

    if (this.state.addons[addonId]) {
      this.state.addons[addonId].value = input.value.trim();
      this.logger.log(`✍️ Text addon ${addonId}:`, input.value);
      this.onPriceStateChange();
    }
  }

  onVisualStateChange() {
    this.logger.log('🔄 Visual State Change Triggered');
    this.updateUI();
  }

  onPriceStateChange() {
    this.logger.log('🔄 Price-only State Change Triggered');
    this.ui.applyConditionalRules();
    this.ui.updatePrice();
    this.ui.updateSummary();
    this.ui.updateHiddenPropertyInputs();
    this.dispatchStateUpdateEvent();
  }

  dispatchStateUpdateEvent() {
    if (!this.container) return;
    this.container.dispatchEvent(new CustomEvent('specifikit:stateUpdated', {
      bubbles: true,
      detail: {
        state: this.state,
        totalPrice: this.getTotalPrice()
      }
    }));
  }

  updateUI() {
    this.ui.applyConditionalRules();
    this.ui.updateImage();
    this.ui.updatePrice();
    this.ui.updateSummary();
    this.ui.updateHiddenPropertyInputs();
    this.dispatchStateUpdateEvent();
  }

  handleFormSubmit(e) {
    e.preventDefault();
    if (this.isSubmitting) return;

    if (this.state.isInitialState) {
      this.ui.showNotification('Please make a selection to customize your product.', 'info');
      this.ui.signalMissingSelection();
      return;
    }

    const requiredComponents = [];
    this.config.components?.forEach(c => {
      if (c.enabled && c.display_style !== 'checkbox') {
        requiredComponents.push(c.key);
      }
    });

    const missing = requiredComponents.filter(k =>
      k === 'size' ? !this.state.size : !this.state.componentSelections[k]
    );

    if (missing.length > 0) {
      this.ui.showNotification(`Please select: ${missing.map(this.capitalize).join(', ')}`, 'error');
      return;
    }

    this.isSubmitting = true;
    this.ui.setSubmitButtonState('loading');
    this.addToCart();
  }

  async addToCart() {
    const items = [];
    const quantity = this.ui.elements.quantityInput ? parseInt(this.ui.elements.quantityInput.value, 10) || 1 : 1;

    if (quantity < 1) {
      this.ui.showNotification('Quantity must be at least 1.', 'error');
      this.isSubmitting = false;
      this.ui.setSubmitButtonState('idle');
      return;
    }

    const mainProperties = {};
    const customPropertyKeys = [];
    const textAddonLabels = [];

    this.container.querySelectorAll('[data-specifikit-prop-input]').forEach(input => {
      if (input.value) {
        const label = input.name.match(/properties\[(.*)\]/)[1];
        mainProperties[label] = input.value;
        customPropertyKeys.push(label);
      }
    });

    for (const [addonId, addon] of Object.entries(this.state.addons)) {
      if (addon.type === 'text' && addon.value) {
        const prefixedLabel = '_' + addon.label;
        mainProperties[prefixedLabel] = addon.value;
        customPropertyKeys.push(prefixedLabel);
        textAddonLabels.push(prefixedLabel);
      }
    }

    // =========================================================================
    // This block ensures the pre-order lead time from the hidden input is
    // correctly added to the properties object sent via the Cart AJAX API.
    // It dynamically reads the property name, making it unbreakable.
    // =========================================================================
    const preorderInput = this.container.querySelector('[data-specifikit-preorder-prop]');
    if (preorderInput && preorderInput.value) {
      const propNameMatch = preorderInput.name.match(/properties\[(.*)\]/);
      if (propNameMatch && propNameMatch[1]) {
        mainProperties[propNameMatch[1]] = preorderInput.value;
        this.logger.log(`📦 Pre-order property added to cart: ${propNameMatch[1]} = ${preorderInput.value}`);
      }
    }

    mainProperties['_customizations'] = customPropertyKeys.join(',');
    if (textAddonLabels.length > 0) {
      mainProperties['_text_addons'] = textAddonLabels.join(',');
    }
    
    // =========================================================================
    // START: PRICE ANCHORING
    // This block adds hidden properties to the line item that anchor the correct
    // price information, allowing our cart-side script to display the accurate
    // total. This is the foundation of the unbreakable price correction system.
    // =========================================================================
    const finalPriceCents = Math.round(this.getTotalPrice() * 100);
    const basePriceCents = Math.round(this.state.basePrice * 100);
    mainProperties['_specifikit_total_price'] = finalPriceCents;
    mainProperties['_specifikit_base_price'] = basePriceCents; // For context and potential future features
    this.logger.info(`💰 Price Anchors Captured: Total=${finalPriceCents}, Base=${basePriceCents}`);
    // END: PRICE ANCHORING

    // =========================================================================
    // START: IMAGE URL CAPTURE
    // This block captures the current image URL and adds it as a hidden property.
    // The underscore prefix ensures it's ignored by the cart display snippet.
    // This is the foundation of the entire cart image update solution.
    // =========================================================================
    const activeScope = this.ui.modal.isOpen && this.ui.modal.instance ? this.ui.modal.instance : this.container;
    const currentImage = activeScope.querySelector('[data-specifikit-image-simple]:not([style*="display: none"]), [data-specifikit-image-layer-stack] img:last-of-type');
    if (currentImage && currentImage.src && !currentImage.src.includes('data:image')) {
      mainProperties['_specifikit_image_url'] = new URL(currentImage.src).href;
      this.logger.info('🖼️ Custom image URL captured for cart/email:', mainProperties['_specifikit_image_url']);
    } else {
      this.logger.warn('Could not capture a valid image URL for the cart.');
    }
    // =========================================================================
    // END: IMAGE URL CAPTURE
    // =========================================================================


    const mainItem = {
      id: this.state.variantId,
      quantity: quantity,
      properties: mainProperties
    };
    items.push(mainItem);

    for (const [addonId, addon] of Object.entries(this.state.addons)) {
      if (addon.type === 'checkbox' && addon.enabled && addon.variantId) {
        items.push({
          id: this.gidToId(addon.variantId),
          quantity: quantity
        });
      }
    }

    const body = {
      items,
      sections: ['cart-drawer', 'cart-icon-bubble', 'header'].join(',')
    };

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(body)
      });
      const cartData = await response.json();
      if (!response.ok) throw new Error(cartData.description || 'Add to cart failed');

      // TRIGGER FOR ADMIN CART CHECKER
      // Dispatches a custom event that the advanced module listens for.
      document.dispatchEvent(new CustomEvent('specifikit:adminCartCheck'));

      if (this.config.displayMode === 'modal' && this.ui.modal.isOpen) {
        window.location.href = '/cart';
        return;
      }

      this.ui.showNotification('Added to cart! ✓', 'success');
      this.ui._updateCartUI(cartData);
    } catch (modernError) {
      this.logger.warn('Modern cart API failed. Falling back to classic method.', modernError);
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            items
          })
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.description || 'Add to cart failed');
        }

        // TRIGGER FOR ADMIN CART CHECKER (FALLBACK)
        document.dispatchEvent(new CustomEvent('specifikit:adminCartCheck'));

        if (this.config.displayMode === 'modal' && this.ui.modal.isOpen) {
          window.location.href = '/cart';
          return;
        }

        this.ui.showNotification('Added to cart! ✓', 'success');
        this.ui._updateCartUI(null);
      } catch (fallbackError) {
        this.logger.error('Cart error (Fallback):', fallbackError);
        this.ui.showNotification(fallbackError.message, 'error');
      }
    } finally {
      this.isSubmitting = false;
      this.ui.setSubmitButtonState('idle');
    }
  }

  applyConfiguration(configState) {
    if (!configState) return {
      applied: [],
      failed: [{
        type: 'system',
        reason: 'No config state provided.'
      }]
    };
    this.logger.info('⚙️ Applying configuration:', configState);

    if (this.state.isInitialState) {
      this.state.isInitialState = false;
      this.logger.log('🚪 Exiting initial state due to applying configuration.');
    }

    const applied = [];
    const failed = [];

    const sizeComponent = this.config.components?.find(c => c.key === 'size' && c.enabled);
    if (sizeComponent && configState.size) {
      const sizeInput = this.container.querySelector(`[data-specifikit-option-input="size"][data-size="${configState.size}"]`);
      if (sizeInput && !sizeInput.disabled) {
        this.ui.elements.componentOptions.size.forEach(el => el.checked = false);
        sizeInput.checked = true;
        applied.push({
          type: 'size',
          value: configState.size
        });
      } else if (configState.size) {
        failed.push({
          type: 'size',
          value: configState.size,
          reason: sizeInput ? 'disabled' : 'not_found'
        });
      }
    }

    for (const [key, selection] of Object.entries(configState.componentSelections || {})) {
      const componentConfig = this.config.components.find(c => c.key === key);
      if (!componentConfig) continue;

      if (componentConfig.display_style === 'checkbox') {
        (selection || []).forEach(sel => {
          const compInput = this.container.querySelector(`[data-specifikit-option-input="${key}"][value="${sel.handle}"]`);
          if (compInput && !compInput.disabled) {
            compInput.checked = true;
            applied.push({
              type: 'component',
              key: key,
              value: sel.handle
            });
          } else if (sel.handle) {
            failed.push({
              type: 'component',
              key: key,
              value: sel.handle,
              reason: compInput ? 'disabled' : 'not_found'
            });
          }
        });
      } else {
        const compInput = this.container.querySelector(`[data-specifikit-option-input="${key}"][value="${selection.handle}"]`);
        if (compInput && !compInput.disabled) {
          this.ui.elements.componentOptions[key].forEach(el => el.checked = false);
          compInput.checked = true;
          applied.push({
            type: 'component',
            key: key,
            value: selection.handle
          });
        } else if (selection.handle) {
          failed.push({
            type: 'component',
            key: key,
            value: selection.handle,
            reason: compInput ? 'disabled' : 'not_found'
          });
        }
      }
    }

    for (const [id, addonState] of Object.entries(configState.addons || {})) {
      const addonInput = this.container.querySelector(`[data-addon-id="${id}"]`);
      if (addonInput && !addonInput.disabled) {
        if (addonInput.type === 'checkbox') {
          addonInput.checked = addonState.enabled || false;
        } else if (addonInput.type === 'text') {
          addonInput.value = addonState.value || '';
        }
        applied.push({
          type: 'addon',
          id: id,
          value: addonState.enabled || addonState.value
        });
      } else if (addonState.enabled || addonState.value) {
        failed.push({
          type: 'addon',
          id: id,
          reason: addonInput ? 'disabled' : 'not_found'
        });
      }
    }

    this.syncStateFromDOM();
    this.onVisualStateChange();

    const report = {
      applied,
      failed
    };
    this.logger.log('✅ Configuration apply report:', report);
    return report;
  }

  syncStateFromDOM() {
    this.logger.log('🔄 Syncing state from DOM after configuration change');

    const checkedSize = this.container.querySelector('[data-specifikit-option-input="size"]:checked');
    if (checkedSize) {
      this.state.size = checkedSize.dataset.size;
      this.state.variantId = checkedSize.value;
      this.state.basePrice = parseFloat(checkedSize.dataset.price || 0);
      this.state.baseCompareAtPrice = parseFloat(checkedSize.dataset.compareAtPrice || 0);
    }

    this.config.components?.forEach(comp => {
      if (!comp.enabled || comp.key === 'size') return;
      const displayStyle = comp.display_style || 'button';

      if (displayStyle === 'checkbox') {
        this.state.componentSelections[comp.key] = [];
        const checkedInputs = this.container.querySelectorAll(`[data-specifikit-option-input="${comp.key}"]:checked`);
        checkedInputs.forEach(input => {
          this.state.componentSelections[comp.key].push({
            handle: input.value,
            name: input.dataset[`${comp.key}Name`] || this.capitalize(input.value),
            priceModifier: parseFloat(input.dataset.priceModifier || 0)
          });
        });
      } else {
        const checkedInput = this.container.querySelector(`[data-specifikit-option-input="${comp.key}"]:checked`);
        if (checkedInput) {
          this.state.componentSelections[comp.key] = {
            handle: checkedInput.value,
            name: checkedInput.dataset[`${comp.key}Name`] || this.capitalize(input.value),
            priceModifier: parseFloat(checkedInput.dataset.priceModifier || 0)
          };
        } else {
          delete this.state.componentSelections[comp.key];
        }
      }
    });

    this.ui.elements.addonInputs.forEach(input => {
      const addonId = input.dataset.addonId;
      if (this.state.addons[addonId]) {
        if (input.type === 'checkbox') {
          this.state.addons[addonId].enabled = input.checked;
        } else if (input.type === 'text') {
          this.state.addons[addonId].value = input.value.trim();
        }
      }
    });

    this.container.querySelectorAll('[data-specifikit-option-input]').forEach(input => {
      const label = input.closest('label');
      if (label) {
        label.classList.toggle('specifikit-active', input.checked);
      }
    });
  }

  /**
   * Calculates the total customized price.
   * This logic is now independent of the visual 'isInitialState' flag,
   * ensuring price modifications from default selections are always
   * calculated correctly on page load.
   * @returns {number} The total price.
   */
  getTotalPrice() {

    // The price must always be calculated based on the current selections,
    // including the default selections on initial page load.
    let total = this.state.basePrice || 0;

    for (const key in this.state.componentSelections) {
      const selection = this.state.componentSelections[key];
      if (Array.isArray(selection)) {
        selection.forEach(subSelection => {
          if (subSelection && typeof subSelection.priceModifier === 'number') {
            total += subSelection.priceModifier;
          }
        });
      } else if (selection && typeof selection.priceModifier === 'number') {
        total += selection.priceModifier;
      }
    }

    for (const addonId in this.state.addons) {
      const addon = this.state.addons[addonId];
      if (addon && typeof addon.price === 'number' && addon.price > 0) {
        if ((addon.type === 'checkbox' && addon.enabled) || (addon.type === 'text' && addon.value)) {
          total += addon.price;
        }
      }
    }

    return total > 0 ? total : 0;
  }

  /**
   * Calculates the total compare-at price.
   * This method correctly adds all price modifiers to the base compare-at price,
   * perfectly mimicking Shopify's native behavior to maintain a consistent discount.
   * Now independent of the visual 'isInitialState' flag for accuracy on page load.
   * @returns {number} The total compare-at price, or 0 if not applicable.
   */
  getTotalCompareAtPrice() {

    // The compare-at price must be calculated if a base compare-at price exists,
    // regardless of the visual state of the image preview.
    // Failsafe 1: If the base product is not on sale, there is no compare-at price.
    if (this.state.baseCompareAtPrice <= 0) {
      return 0;
    }

    let total = this.state.baseCompareAtPrice;
    let modifiersTotal = 0;

    // Calculate the sum of all price modifiers from components.
    for (const key in this.state.componentSelections) {
      const selection = this.state.componentSelections[key];
      if (Array.isArray(selection)) { // Handle multi-select checkboxes
        selection.forEach(subSelection => {
          if (subSelection && typeof subSelection.priceModifier === 'number') {
            modifiersTotal += subSelection.priceModifier;
          }
        });
      } else if (selection && typeof selection.priceModifier === 'number') { // Handle single-select radio/dropdowns
        modifiersTotal += selection.priceModifier;
      }
    }

    // Calculate the sum of all price modifiers from add-ons.
    for (const addonId in this.state.addons) {
      const addon = this.state.addons[addonId];
      if (addon && typeof addon.price === 'number' && addon.price > 0) {
        if ((addon.type === 'checkbox' && addon.enabled) || (addon.type === 'text' && addon.value)) {
          modifiersTotal += addon.price;
        }
      }
    }

    // Apply the total modifier to the base compare-at price.
    total += modifiersTotal;

    // Failsafe 2: The compare-at price must be greater than the final sale price.
    // If it's not (e.g., due to large negative modifiers making the discount disappear),
    // we return 0 to hide the compare-at price entirely, avoiding customer confusion.
    const finalSalePrice = this.getTotalPrice();
    if (total <= finalSalePrice) {
      this.logger.log(`Compare-at price (${total}) is not greater than sale price (${finalSalePrice}). Hiding it.`);
      return 0;
    }

    // Failsafe 3: Ensure the returned value is never negative.
    return total > 0 ? total : 0;
  }

  loadImage(src) {
    return new Promise((resolve, reject) => {
      if (this.imageCache.has(src)) {
        const cachedImg = this.imageCache.get(src);
        if (cachedImg.complete) {
          resolve(cachedImg);
        } else {
          cachedImg.onload = () => resolve(cachedImg);
          cachedImg.onerror = reject;
        }
        return;
      }
      const img = new Image();
      img.onload = () => {
        this.imageCache.set(src, img);
        resolve(img);
      };
      img.onerror = reject;
      img.src = src;
    });
  }

  // =========================================================================
  // START: CART PRICE & IMAGE ENGINE
  // This module is a self-contained, theme-agnostic engine for ensuring that
  // customized product data is always correctly displayed in any cart,
  // drawer, or popup across any Shopify theme.
  // =========================================================================
  initCartObserver() {
    if (this.cartObserver) return; // Prevent multiple initializations
    this.logger.info('🛒 Initializing Unbreakable Cart Observer (for Images & Prices).');

    const observerCallback = (mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          for (const node of mutation.addedNodes) {
            if (node.nodeType === 1 && (node.closest('form[action*="/cart"], [data-cart-wrapper]') || node.matches('form[action*="/cart"], [data-cart-wrapper]'))) {
              this.logger.log('Cart-related DOM mutation detected. Triggering update.');
              this.triggerCartUpdate();
              return; // Trigger once per mutation batch
            }
          }
        }
      }
    };

    this.cartObserver = new MutationObserver(observerCallback);
    this.cartObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    if (document.location.pathname.startsWith('/cart')) {
      this.logger.log('Cart page detected on initial load. Scheduling update.');
      setTimeout(() => this.triggerCartUpdate(), 250);
    }

    document.body.addEventListener('cart:updated', () => {
      this.logger.log('🛍️ "cart:updated" event detected. Triggering update...');
      this.triggerCartUpdate();
    });
  }

  triggerCartUpdate() {
    if (this.cartUpdateTimeout) {
      clearTimeout(this.cartUpdateTimeout);
    }
    this.cartUpdateTimeout = setTimeout(() => {
      this.updateCartImages();
      this.updateCartPrices();
    }, 150);
  }

  async updateCartPrices() {
    if (this.isUpdatingCartPrices) {
        this.logger.log('Price update already in progress. Skipping.');
        return;
    }
    this.isUpdatingCartPrices = true;
    this.logger.groupCollapsed('💰 Starting Specifikit Unbreakable Cart Price Engine...');

    try {
        const res = await fetch('/cart.js');
        if (!res.ok) throw new Error(`Cart fetch failed with status ${res.status}`);
        const cart = await res.json();
        
        let calculatedSubtotalCents = 0;
        let requiresDomUpdate = false;
        let updateTasks = [];

        const cartContainers = this._findCartContainer();

        for (const [index, itemData] of cart.items.entries()) {
            let linePriceCents = itemData.final_line_price; // Shopify's price in cents
            
            const customPriceProp = itemData.properties?._specifikit_total_price;

            if (customPriceProp && !isNaN(customPriceProp)) {
                requiresDomUpdate = true;
                const singleItemPriceCents = parseFloat(customPriceProp);
                const correctLinePriceCents = singleItemPriceCents * itemData.quantity;
                
                // Add this item to the list of tasks to be processed in the DOM
                updateTasks.push({
                    itemData: itemData,
                    itemIndex: index,
                    correctSinglePriceCents: singleItemPriceCents,
                    correctLinePriceCents: correctLinePriceCents,
                });
                
                // FAILSAFE: Always use our correct calculated price for the subtotal.
                linePriceCents = correctLinePriceCents;
            }
            calculatedSubtotalCents += linePriceCents;
        }
        
        if (requiresDomUpdate) {
            this.logger.log(`Found ${updateTasks.length} item(s) requiring price correction.`);
            
            // Perform DOM updates for each item
            for (const task of updateTasks) {
                const itemElement = this._findCartItemElement(task.itemData, task.itemIndex, cartContainers, cart.items);

                if (itemElement) {
                    // START: INTELLIGENCE CHECK
                    // If the Liquid "Instant Render" snippet is present, do nothing.
                    if (itemElement.querySelector('[data-specifikit-price-correct="true"]')) {
                        this.logger.log(`💰 Skipping JS price update for "${task.itemData.product_title}". Liquid render detected.`);
                        continue; // Move to the next item
                    }
                    // END: INTELLIGENCE CHECK

                    this._updatePriceInDom(
                        itemElement, 
                        task.itemData,
                        task.correctSinglePriceCents, 
                        task.correctLinePriceCents
                    );
                } else {
                    this.logger.warn(`Could not find DOM element for item "${task.itemData.product_title}" to update its price. Subtotal will still be corrected.`);
                }
            }
            
            // After all line items are corrected, update the subtotal
            this._updateCartSubtotalInDom(calculatedSubtotalCents, cart.total_price, cart.currency);
        } else {
             this.logger.log('No Specifikit items found needing price correction.');
        }

    } catch (e) {
        this.logger.error('Cart Price Engine Error:', e);
    } finally {
        setTimeout(() => { this.isUpdatingCartPrices = false; }, 500); // Prevent rapid re-runs
        this.logger.log('✅ Cart Price Engine process finished.');
        this.logger.groupEnd();
    }
  }

  /**
   * DOM Price Updater with Multi-Layered Failsafes
   */
  _updatePriceInDom(itemElement, itemData, singlePriceCents, linePriceCents) {
    const formattedSinglePrice = this.formatMoney(singlePriceCents);
    const formattedLinePrice = this.formatMoney(linePriceCents);
    const incorrectFormattedLinePrice = this.formatMoney(itemData.final_line_price);

    this.logger.log(`Updating price for "${itemData.product_title}". Correct: ${formattedLinePrice}, Incorrect: ${incorrectFormattedLinePrice}`);

    // Layer 1: Attempt update using a comprehensive list of selectors.
    const linePriceSelectors = [
        '.cart-item__totals .price', '[data-cart-item-line-price]', '.cart-item__final-price',
        '.cart-item__line-price', '.cart-item-total', '[data-cart-item-total]',
        '.line-item-total', '.item-total .money', '.cart-item-price--total .money',
        '[data-cart-item-regular-price-group] .money' // Dawn theme specific
    ];

    let updateSuccessful = false;
    for (const selector of linePriceSelectors) {
        const priceEls = itemElement.querySelectorAll(selector);
        if (priceEls.length > 0) {
            priceEls.forEach(priceEl => {
                if (priceEl.closest('s, del')) return; // Don't update compare-at prices
                const innerMoney = priceEl.querySelector('.money, [class*="Price"]');
                const target = innerMoney || priceEl;
                if (target.textContent.trim() !== formattedLinePrice) {
                    target.textContent = formattedLinePrice;
                    updateSuccessful = true;
                }
            });
            if (updateSuccessful) {
                this.logger.log(`  L1 Success: Updated line price using selector: ${selector}`);
                break; // Stop after first successful update
            }
        }
    }

    // Layer 2: Failsafe - Find element by content and replace it.
    if (!updateSuccessful) {
        this.logger.warn(`  L1 Failed. Activating L2 Failsafe (Content Search) for "${itemData.product_title}".`);
        const walk = (node) => {
            if (updateSuccessful) return;
            if (node.nodeType === 3) { // Text node
                if (node.textContent.trim() === incorrectFormattedLinePrice) {
                    let parent = node.parentElement;
                    // Go up to a meaningful element, but not past the itemElement boundary.
                    while (parent && parent.children.length === 0 && parent !== itemElement) {
                        parent = parent.parentElement;
                    }
                    if (parent) {
                        parent.textContent = formattedLinePrice;
                        this.logger.log('  L2 Success: Replaced price via text node parent content.', parent);
                        updateSuccessful = true;
                    }
                }
            } else {
                for (let i = 0; i < node.childNodes.length; i++) {
                    walk(node.childNodes[i]);
                }
            }
        };
        walk(itemElement);
    }
    
    // Layer 3: Final Failsafe - Hide original price, show our own.
    if (!updateSuccessful) {
        this.logger.error(`  L2 Failed. Activating L3 Failsafe (Visual Override) for "${itemData.product_title}".`);
        const overrideEl = itemElement.querySelector('[data-specifikit-override-price]');
        if (overrideEl) {
            const allPriceElements = itemElement.querySelectorAll('.price, .money, [class*="price"], [class*="Price"], [data-cart-item-final-price]');
            let hiddenSomething = false;
            
            let elementToHide = null;
            // Find the most likely line total element to hide by content match
            for (let i = allPriceElements.length - 1; i >= 0; i--) {
                const el = allPriceElements[i];
                if (!el.closest('s, del') && el.textContent.trim() === incorrectFormattedLinePrice) {
                    elementToHide = el;
                    break;
                }
            }
            
            // If not found by content, fall back to the last price-like element
            if (!elementToHide) {
                for (let i = allPriceElements.length - 1; i >= 0; i--) {
                    const el = allPriceElements[i];
                    if (!el.closest('s, del')) {
                        elementToHide = el;
                        break;
                    }
                }
            }

            if (elementToHide) {
                this.logger.log(`  L3: Identified element to hide:`, elementToHide);
                const parent = elementToHide.parentElement;
                if (parent && parent.innerText.trim() === elementToHide.innerText.trim() && parent.children.length === 1) {
                    parent.style.display = 'none';
                } else {
                    elementToHide.style.display = 'none';
                }
                hiddenSomething = true;
            }

            if (hiddenSomething) {
                overrideEl.style.display = 'block';
                this.logger.log('  L3 Success: Visually replaced incorrect price with failsafe element.');
            } else {
                 this.logger.error('  L3 Failed: Could not find any element to hide. Price may remain incorrect.');
            }
        }
    }
    
    // Also update the single item price if it exists. This is less critical but good for UX.
    const singlePriceSelectors = [
      '.cart-item__price-wrapper .price:not(.price--compare)', '.cart-item__price .price', 
      '.cart-item-price', '[data-cart-item-price]', '.product-price .price', '[data-product-price]'
    ];
    for (const selector of singlePriceSelectors) {
        const el = itemElement.querySelector(selector);
        if (el && !el.closest('s, del')) {
            const target = el.querySelector('.money, [class*="Price"]') || el;
            if (target.textContent.trim() !== formattedSinglePrice) {
               target.textContent = formattedSinglePrice;
            }
            break;
        }
    }
  }

  /**
   * DOM Subtotal Updater with Multi-Layered Failsafes
   */
  _updateCartSubtotalInDom(correctSubtotalCents, incorrectSubtotalCents, currency) {
    // START: INTELLIGENCE CHECK
    if (document.querySelector('[data-specifikit-subtotal-correct="true"]')) {
        this.logger.log('💰 Skipping JS subtotal update. Liquid render detected.');
        return;
    }
    // END: INTELLIGENCE CHECK

    const formattedCorrectSubtotal = this.formatMoney(correctSubtotalCents);
    const formattedIncorrectSubtotal = this.formatMoney(incorrectSubtotalCents);
    this.logger.log(`Updating subtotal. Correct: ${formattedCorrectSubtotal}, Incorrect from API: ${formattedIncorrectSubtotal}`);
    
    // Layer 1: Selector-based update
    const subtotalSelectors = [
        '[data-cart-subtotal]', '.cart-subtotal__price', '.totals__subtotal-value',
        '.cart__subtotal', '.subtotal-price', '#cart-subtotal', '[data-subtotal]',
        '.cart-drawer__subtotal', '.cart-original-total', '.js-cart-subtotal'
    ];
    
    let subtotalUpdated = false;
    for (const selector of subtotalSelectors) {
        const subtotalEls = document.querySelectorAll(selector);
        if (subtotalEls.length > 0) {
            subtotalEls.forEach(subtotalEl => {
                const innerPrice = subtotalEl.querySelector('.money, .price, [class*="Price"], [data-cart-price]');
                const target = innerPrice || subtotalEl;
                if (target.textContent.match(/[\d,.]/)) {
                    if (target.textContent.trim() !== formattedCorrectSubtotal) {
                        target.textContent = formattedCorrectSubtotal;
                        subtotalUpdated = true;
                    }
                }
            });
            if (subtotalUpdated) {
                this.logger.log(`  L1 Success: Updated subtotal using selector: ${selector}`);
                return; // Exit after first successful update
            }
        }
    }

    // Layer 2: Content-based update failsafe
    if (!subtotalUpdated) {
        this.logger.warn('  L1 Subtotal update failed. Activating L2 Failsafe (Content Search).');
        const allElements = document.querySelectorAll('body *:not(script):not(style)');
        for (let i = allElements.length - 1; i >= 0; i--) {
            const el = allElements[i];
            if (el.children.length === 0 && el.textContent.trim() === formattedIncorrectSubtotal) {
                const parentText = el.parentElement.textContent.toLowerCase();
                if (parentText.includes('subtotal') || parentText.includes('total')) {
                    el.textContent = formattedCorrectSubtotal;
                    subtotalUpdated = true;
                    this.logger.log('  L2 Success: Found and replaced subtotal via content match.', el);
                    return; // Exit on first success
                }
            }
        }
    }

    if (!subtotalUpdated) {
        this.logger.error('  L1 & L2 Failed. Could not find a cart subtotal element to update.');
    }
  }


  async updateCartImages() {
    if (this.isUpdatingCartImages) {
      this.logger.log('Image update already in progress. Skipping.');
      return;
    }
    this.isUpdatingCartImages = true;
    this.logger.groupCollapsed('🚀 Starting cart image update process...');

    try {
      const res = await fetch('/cart.js');
      if (!res.ok) throw new Error(`Cart fetch failed with status ${res.status}`);
      const cart = await res.json();

      const specifikitItems = cart.items
        .map((item, index) => ({ ...item,
          originalIndex: index
        }))
        .filter(item => item.properties && item.properties._customizations);

      if (specifikitItems.length === 0) {
        this.logger.log('No customizable items found in cart.');
        this.logger.groupEnd();
        this.isUpdatingCartImages = false;
        return;
      }

      this.logger.log(`Found ${specifikitItems.length} customizable item(s) to process.`);
      document.querySelectorAll('[data-specifikit-processed]').forEach(el => delete el.dataset.specifikitProcessed);

      const cartContainers = this._findCartContainer();
      if (cartContainers.length === 0) {
        this.logger.warn('Could not find a visible cart container. Searches will be global.');
      }

      for (const itemData of specifikitItems) {
        const itemElement = this._findCartItemElement(itemData, itemData.originalIndex, cartContainers, cart.items);

        if (itemElement) {
          itemElement.dataset.specifikitProcessed = 'true';

          // START: INTELLIGENCE CHECK
          // If the Liquid "Instant Render" snippet is present, do nothing.
          if (itemElement.querySelector('[data-specifikit-image-correct="true"]')) {
              this.logger.log(`🖼️ Skipping JS image update for "${this.escapeHtml(itemData.product_title)}". Liquid render detected.`);
              continue; // Move to the next item
          }
          // END: INTELLIGENCE CHECK

          const imageContainer = itemElement.querySelector('[class*="image"], .cart-item__media, .cart-item__image-container');
          const imgElement = itemElement.querySelector('img');
          const customImageUrl = itemData.properties._specifikit_image_url;

          if (customImageUrl) {
            if (imgElement) {
              if (imgElement.dataset.specifikitUpdatedUrl !== customImageUrl) {
                this._updateImageElement(imgElement, customImageUrl, imageContainer || itemElement, itemData);
              } else {
                this.logger.log(`Image for "${this.escapeHtml(itemData.product_title)}" is already correct.`);
              }
            } else {
              this._watchAndInject(itemElement, itemData, imageContainer);
            }
          } else {
            if (!imgElement) {
              this._watchAndInject(itemElement, itemData, imageContainer);
            }
          }
        }
      }
    } catch (e) {
      this.logger.error('Cart Image Updater Error:', e);
    } finally {
      setTimeout(() => {
        this.isUpdatingCartImages = false;
        document.querySelectorAll('[data-specifikit-processed]').forEach(el => delete el.dataset.specifikitProcessed);
      }, 500);
      this.logger.log('✅ Cart image update process finished.');
      this.logger.groupEnd();
    }
  }

  _findCartContainer() {
    const selectors = 'form[action*="/cart"], [data-cart-wrapper], .cart-drawer, #cart-drawer-container, #cart, [data-section-type*="cart"]';
    const containers = Array.from(document.querySelectorAll(selectors));
    const visibleContainers = containers.filter(el => {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0;
    });
    return visibleContainers.length > 0 ? visibleContainers : containers;
  }

  _findCartItemElement(itemData, itemIndex, cartContainers, allCartItems) {
    const itemKey = itemData.key;
    const variantId = itemData.variant_id;
    const searchScopes = cartContainers.length > 0 ? cartContainers : [document.body];

    const primarySelectors = [
      `[data-cart-item][data-key="${itemKey}"]`,
      `[data-cart-item-key="${itemKey}"]`,
      `[data-line-item-key="${itemKey}"]`,
      `[data-line-key="${itemKey}"]`,
      `[data-line-id="${itemKey}"]`,
      `[id="CartItem-${itemKey}"]`,
      `[id="line-item-${itemKey}"]`
    ];
    for (const scope of searchScopes) {
      for (const selector of primarySelectors) {
        try {
          const element = scope.querySelector(selector);
          if (element && !element.dataset.specifikitProcessed) {
            this.logger.log(`Found item [${itemKey}] via primary selector: ${selector}`);
            return element;
          }
        } catch (e) {
          this.logger.warn(`Invalid selector attempted: ${selector}`, e.message);
        }
      }
    }

    for (const scope of searchScopes) {
      const links = scope.querySelectorAll(`a[href*="/variants/${variantId}"], a[href*="?variant=${variantId}"]`);
      for (const link of links) {
        const potentialItem = link.closest('[class*="cart-item"], [class*="cart__item"], tr, li');
        if (potentialItem && !potentialItem.dataset.specifikitProcessed) {
          if (potentialItem.querySelector('[name*="updates"], [data-quantity-input], .cart-item__quantity-input')) {
            this.logger.log(`Found item [${itemKey}] via variant link failsafe.`);
            return potentialItem;
          }
        }
      }
    }

    for (const scope of searchScopes) {
      const allRows = scope.querySelectorAll('.cart-item, .cart__item, [data-cart-item]');
      if (allRows.length === allCartItems.length) {
        const element = allRows[itemIndex];
        if (element && !element.dataset.specifikitProcessed) {
          this.logger.log(`Found item [${itemKey}] via direct indexing.`);
          return element;
        }
      }
    }

    this.logger.warn(`Could not find DOM element for item [${itemKey}] with any method.`);
    return null;
  }

  _watchAndInject(itemElement, itemData, imageContainer) {
    const itemKey = itemData.key;
    if (this.watchingForImages.has(itemKey)) return;

    this.logger.log(`🕵️ Image tag missing or delayed for item ${itemKey}. Starting resilient watcher...`);
    this.watchingForImages.add(itemKey);

    let attempts = 0;
    const maxAttempts = 50; // 5 seconds timeout

    const intervalId = setInterval(() => {
      const currentItemElement = document.body.contains(itemElement) ? itemElement : this._findCartItemElement(itemData, itemData.originalIndex, this._findCartContainer(), []);
      if (!currentItemElement) {
        clearInterval(intervalId);
        this.watchingForImages.delete(itemKey);
        this.logger.warn(`Watcher stopped for item ${itemKey}. Parent element was removed from the DOM.`);
        return;
      }

      const imgElement = currentItemElement.querySelector('img, picture > source');
      attempts++;

      const customImageUrl = itemData.properties._specifikit_image_url;

      if (imgElement) {
        clearInterval(intervalId);
        this.watchingForImages.delete(itemKey);
        this.logger.log(`Watcher found image for item ${itemKey} after ${attempts} attempt(s).`);

        if (customImageUrl) {
          this._updateImageElement(imgElement, customImageUrl, imageContainer || currentItemElement, itemData);
        } else {
          this.logger.log(`Theme rendered an image for ${itemKey}. No custom URL found, no action needed.`);
        }

      } else if (attempts >= maxAttempts) {
        clearInterval(intervalId);
        this.watchingForImages.delete(itemKey);
        this.logger.warn(`❌ Watcher timed out for item ${itemKey}. No image tag found. Force-injecting.`);

        const targetContainer = this._findOrInjectImageCell(currentItemElement);

        if (targetContainer) {
          if (customImageUrl) {
            this.logger.log(`🎨 Injecting custom image directly into failsafe cell for item ${itemKey}.`);
            this._updateImageElement(null, customImageUrl, targetContainer, itemData);
          } else {
            this.logger.log(`🎨 Injecting "Image Unavailable" placeholder into failsafe cell for item ${itemKey}.`);
            this._injectPlaceholder(targetContainer, itemData);
          }
        }
      }
    }, 100);
  }

  _findOrInjectImageCell(itemElement) {
    const existingCell = itemElement.querySelector('.cart-item__media, [class*="--media"], [class*="image-container"]');
    if (existingCell) {
      return existingCell;
    }

    this.logger.log("No standard image cell found. Creating a structurally-aware failsafe cell.");

    if (itemElement.tagName === 'TR') {
      const newCell = document.createElement('td');
      newCell.className = 'cart-item__media specifikit-injected-cell';
      newCell.style.cssText = 'width: 100px; max-width: 25%; padding-right: 15px; vertical-align: top;';
      itemElement.prepend(newCell);
      return newCell;
    }

    if (itemElement.tagName === 'DT' && itemElement.parentElement.tagName === 'DL') {
      const newCell = document.createElement('dd');
      newCell.className = 'cart-item__media specifikit-injected-cell';
      newCell.style.cssText = 'margin: 0; padding: 0;';
      itemElement.insertAdjacentElement('afterend', newCell);
      return newCell;
    }

    const newContainer = document.createElement('div');
    newContainer.className = 'cart-item__media specifikit-injected-container';
    newContainer.style.cssText = 'flex-basis: 100px; flex-shrink: 0; width: 100px; max-width: 25%; margin-right: 15px;';
    itemElement.prepend(newContainer);
    return newContainer;
  }

  _injectPlaceholder(container, itemData) {
    if (container.dataset.specifikitPlaceholderInjected) return;

    const placeholderText = this._getI18nText('preview-unavailable', 'Preview Unavailable');
    const ariaLabel = `${placeholderText} for ${this.escapeHtml(itemData.product_title)}`;

    container.innerHTML = `
      <a href="${itemData.url}" class="specifikit-injected-link" style="display: block; text-decoration: none;">
        <div data-specifikit-placeholder-injected="true" role="img" aria-label="${ariaLabel}" style="width:100%; height:100%; display:flex; flex-direction:column; align-items:center; justify-content:center; background-color:#f3f4f6; border:1px solid #e5e7eb; border-radius: inherit; aspect-ratio:4/5;">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" style="width:30%; height:30%; max-width:40px; color:#9ca3af;">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 15a3 3 0 100-6 3 3 0 000 6z" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M2.089 2.089l19.823 19.823" />
            </svg>
            <span style="font-size:10px; color:#9ca3af; margin-top:4px; text-align:center; line-height: 1.2;">${placeholderText}</span>
        </div>
      </a>
    `;
    container.dataset.specifikitUpdatedUrl = 'placeholder';
    container.dataset.specifikitPlaceholderInjected = 'true';
  }

  _updateImageElement(imgElement, newUrl, container, itemData) {
    if (imgElement) {
      if (imgElement.dataset.specifikitUpdatedUrl === newUrl) {
        this.logger.log(`Image for "${this.escapeHtml(itemData.product_title)}" is already up to date.`);
        return;
      }
      this.logger.log(`🖼️ Updating existing image for "${this.escapeHtml(itemData.product_title)}".`);
      this._updateExistingImage(imgElement, newUrl, itemData);
      return;
    }

    if (container) {
      this.logger.log(`🎨 Injecting new image for "${this.escapeHtml(itemData.product_title)}".`);
      this._injectNewImageElement(container, newUrl, itemData);
    } else {
      this.logger.error('Cannot update image, no container element provided.');
    }
  }

  _updateExistingImage(imgElement, newUrl, itemData) {
    const createSizedUrl = (url, size) => {
      if (!url || typeof url !== 'string') return '';
      try {
        const urlParts = url.split('?');
        const baseUrl = urlParts[0];
        const query = urlParts.length > 1 ? `?${urlParts[1]}` : '';
        const extensionMatch = baseUrl.match(/\.(jpg|jpeg|png|gif|webp|heic|heif|avif)/i);
        if (!extensionMatch) return url;
        const extension = extensionMatch[0];
        const pathWithoutExt = baseUrl.substring(0, baseUrl.length - extension.length);
        const pathWithoutSize = pathWithoutExt.replace(/_(\d+x\d*|pico|icon|thumb|small|compact|medium|large|grande|original)$/i, '');
        return `${pathWithoutSize}_${size}x${extension}${query}`;
      } catch (e) {
        this.logger.warn('Could not parse URL for resizing.', url, e);
        return url;
      }
    };
    const createSrcset = (url) => {
      const sizes = [150, 300, 450, 600];
      return sizes.map(size => `${createSizedUrl(url, size)} ${size}w`).join(', ');
    };

    const sizedNewUrl = createSizedUrl(newUrl, 300);
    const newSrcset = createSrcset(newUrl);
    const altText = this._getI18nText('alt-text', 'Customized Product').replace('Customized Product', `Customized ${this.escapeHtml(itemData.product_title)}`);

    const visualRoot = imgElement.closest('picture') || imgElement;
    
    visualRoot.style.transition = 'opacity 0.3s ease-in-out';
    visualRoot.style.opacity = '0';

    const onImageLoad = () => {
        visualRoot.style.opacity = '1';
        imgElement.removeEventListener('load', onImageLoad);
        imgElement.removeEventListener('error', onImageError); // Clean up error handler
        const updateEvent = new CustomEvent('specifikit:imageUpdated', { bubbles: false });
        imgElement.dispatchEvent(updateEvent);
        this.logger.log(`Dispatched non-bubbling 'specifikit:imageUpdated' event for item "${itemData.key}".`);
    };
    
    const onImageError = () => {
        this.logger.warn(`Failed to load custom cart image: ${sizedNewUrl}. The original image will be shown as a failsafe.`);
        visualRoot.style.opacity = '1'; // Re-show the old image as a failsafe
        imgElement.removeEventListener('load', onImageLoad);
        imgElement.removeEventListener('error', onImageError);
    };

    imgElement.addEventListener('load', onImageLoad);
    imgElement.addEventListener('error', onImageError);
    
    imgElement.alt = altText;
    if (imgElement.tagName === 'IMG') {
        imgElement.src = sizedNewUrl;
        if (imgElement.hasAttribute('srcset')) imgElement.srcset = newSrcset;
        if (imgElement.hasAttribute('data-src')) imgElement.dataset.src = sizedNewUrl;
        if (imgElement.hasAttribute('data-srcset')) imgElement.dataset.srcset = newSrcset;
    }

    const picture = imgElement.closest('picture');
    if (picture) {
      picture.querySelectorAll('source').forEach(source => {
        if (source.hasAttribute('srcset')) source.srcset = newSrcset;
        if (source.hasAttribute('data-srcset')) source.dataset.srcset = newSrcset;
      });
      const fallbackImg = picture.querySelector('img');
      if (fallbackImg) {
        fallbackImg.src = sizedNewUrl;
        fallbackImg.alt = altText;
      }
    }
    
    if (imgElement.complete) {
        onImageLoad();
    }
    
    (picture || imgElement).dataset.specifikitUpdatedUrl = newUrl;
  }

  _injectNewImageElement(container, newUrl, itemData) {
    const sizedUrl = newUrl.replace(/_(\d+)x\d*/, '_300x');
    const altText = this._getI18nText('alt-text', 'Customized Product').replace('Customized Product', `Customized ${this.escapeHtml(itemData.product_title)}`);

    container.innerHTML = '';

    const link = document.createElement('a');
    link.href = itemData.url;
    link.className = 'specifikit-injected-link';
    link.style.display = 'block';
    link.style.lineHeight = '0';
    
    const newImg = document.createElement('img');
    newImg.src = sizedUrl;
    newImg.alt = altText;
    newImg.dataset.specifikitUpdatedUrl = newUrl;
    
    newImg.style.cssText = 'width:100%; height:auto; object-fit:contain; aspect-ratio:4/5; display:block; border-radius:inherit; opacity: 0; transition: opacity 0.3s ease-in-out;';

    newImg.onload = () => {
        newImg.style.opacity = '1';
    };
    newImg.onerror = () => {
        this.logger.warn(`Failed to load injected cart image: ${sizedUrl}. Injecting placeholder as failsafe.`);
        this._injectPlaceholder(container, itemData);
    };

    link.appendChild(newImg);
    container.appendChild(link);
    
    if (newImg.complete) {
        newImg.onload();
    }
    
    const injectEvent = new CustomEvent('specifikit:imageUpdated', { bubbles: false });
    newImg.dispatchEvent(injectEvent);
    this.logger.log(`Injected new image with fade-in and dispatched non-bubbling 'specifikit:imageUpdated' event for item "${itemData.key}".`);
  }

  _getI18nText(key, fallback) {
    const el = document.getElementById(`specifikit-cart-i18n-${key}`);
    return el?.textContent?.trim() || fallback;
  }
  // =========================================================================
  // END: CART PRICE & IMAGE ENGINE
  // =========================================================================

  gidToId(gid) {
    if (!gid || typeof gid !== 'string') return null;
    return gid.split('/').pop();
  }

  formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');

    const formatString = format || this.config?.moneyFormat || window.Shopify?.currency?.money_format || '${{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    const defaultOption = (opt, def) => (typeof opt === 'undefined' || opt === null) ? def : opt;

    const formatWithDelimiters = (number, precision, thousands, decimal) => {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');

      if (isNaN(number) || number == null) return '0';

      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return dollars + (parts[1] ? (decimal + parts[1]) : '');
    };

    let value = '';
    const match = formatString.match(placeholderRegex);
    if (!match) return formatString;

    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  capitalize(str) {
    if (!str || typeof str !== 'string') return '';
    return str.replace(/-/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
  }

  // Self-contained helper to avoid dependency on the UI class
  escapeHtml(str) {
    if (!str) return '';
    const p = document.createElement('p');
    p.textContent = str;
    return p.innerHTML;
  }
}

// Set the class on the window object so the guard clause can find it.
window.SpecifikitCore = SpecifikitCore;

document.addEventListener('DOMContentLoaded', () => {
  const customizerWrapper = document.querySelector('[data-specifikit-container]');
  
  if (window.specifikitCoreInstance) {
    // Already initialized by another script or event.
    return;
  }

  window.specifikitCoreInstance = new SpecifikitCore();

  if (customizerWrapper) {
    if (document.getElementById('CustomizerConfig')) {
      window.specifikitCoreInstance.logger.info('DOM Ready - Full customizer instance is active.');
      document.dispatchEvent(new CustomEvent('specifikitCore:loaded', {
        bubbles: true,
        detail: {
          instance: window.specifikitCoreInstance
        }
      }));
    } else {
      window.specifikitCoreInstance.logger.error('Config data island missing from the page. The Liquid pre-flight check may have removed the customizer.');
    }
  } else {
    window.specifikitCoreInstance.logger.log('DOM Ready - Customizer container not found on this page. Cart observer is active.');
  }
});

} // End of Idempotent Script Guard

/**
 * ============================================================================
 * END Specifikit Core JS FILE
 * ============================================================================
 */